import { API_BASE_URL } from "@/config";
import axios from "axios";
import { getCookie } from "@/utils/cookies";

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        "Accept-Language": "es",
        "Content-Type": "application/json",
    },
});

export const setAccessToken = (token: string | null) => {
    localStorage.setItem("accessToken", token || "");
};

axiosInstance.interceptors.request.use(
    (config) => {
        const accessToken = localStorage.getItem("accessToken");
        const csrfToken = getCookie("csrftoken");

        if (accessToken && !config.url?.includes("/auth/refresh/")) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }

        if (
            csrfToken &&
            config.method &&
            !["GET", "HEAD", "OPTIONS"].includes(config.method.toUpperCase())
        ) {
            config.headers["X-CSRFToken"] = csrfToken;
        }

        return config;
    },
    (error) => Promise.reject(error),
);

let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (reason: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const isLoginOrRefreshAttempt =
            originalRequest.url?.includes("/auth/login/") ||
            originalRequest.url?.includes("/auth/refresh/");

        if (
            error.response?.status === 401 &&
            !originalRequest._retry &&
            !isLoginOrRefreshAttempt
        ) {
            if (isRefreshing) {
                try {
                    const token = await new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    });
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axiosInstance(originalRequest);
                } catch (queueError) {
                    return Promise.reject(queueError);
                }
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const { data } = await axiosInstance.post("/auth/refresh/");
                const newAccessToken = data.access;

                setAccessToken(newAccessToken);
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                processQueue(null, newAccessToken);

                return axiosInstance(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                setAccessToken(null);
                localStorage.removeItem("user");
                if (!originalRequest.url?.includes("/auth/status/")) {
                    window.location.href = "/login";
                }
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        return Promise.reject(error);
    },
);

export default axiosInstance;
